import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as surveyActions from 'actions/surveyActions';
import * as pageActions from 'actions/pageActions';
import { ENGAGEMENT_SURVEY_TYPE_ID } from 'constants/survey';
import withRequestErrors, { withRequestErrorsPropTypes } from 'components/HOC/withRequestErrors';
import { isBatchSurvey } from 'utils/surveyUtil';
import { ENGAGEMENT_SURVEY_ANSWER_URL } from 'constants/api';
import { GO_TO_MY_REPORT } from 'constants/pageDictionary';
import { ENGLISH } from 'constants/language';
import i18n from 'i18next';
import { getEngagementSurveyTips } from 'actions/tipActions';
import EngagementSurveyHeader from './EngagementSurveyHeader';
import QuestionContent from './QuestionContent';
import AnsweredToggle from './AnsweredToggle';
import CoachingTip from './CoachingTip';
import ThankYouForAnswering from './ThankYouForAnswering';
import InitialExtraQuestions from '../InitialExtraQuestions';
import BackNextToggle from './AnsweredToggle/BackNextToggle';
import './styles.scss';
import { trackMixpanelEvent } from '../../../utils';
import { MIX_PANEL_EVENTS } from '../../../constants/mixpanel';

const { ENGAGEMENT_SURVEY } = MIX_PANEL_EVENTS;

const hasFinishedEngagementQuestions = (lastQuestion, surveyQuestions) => {
    return lastQuestion && (
        (surveyQuestions.length && lastQuestion === surveyQuestions[surveyQuestions.length - 1].id)
            || (!surveyQuestions.map(question => question.id).includes(lastQuestion))
    );
};

class Engagement extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentQuestionIdx: 0,
            surveyQuestions: props.surveyQuestions,
            questionAnswers: new Array(props.surveyQuestions.length),
            questionComments: new Array(props.surveyQuestions.length),
            isShowCoachingTip: false,
            isShowInitialExtraQuestions: false,
            isShowThankYouForAnswering: false,
            isExtraQuestions: false,
            tip: null,
            previousQuestionIdx: 0,
            isNextLocked: false,
            shouldShowControls: true
        };

        this.commentChangeDebounceRef = React.createRef(null);
    }

    componentDidMount() {
        if (this.props.lastQuestion && hasFinishedEngagementQuestions(this.props.lastQuestion, this.props.surveyQuestions) && this.props.extraQuestions.length) {
            this.displayInitialExtraQuestions();
        }
    }

    render() {
        return (
            <div className='engagement-survey-container'>
                <EngagementSurveyHeader
                    shouldHideRightLabel={ this.state.isExtraQuestions || this.state.isShowInitialExtraQuestions }
                    progressTracker={
                        <AnsweredToggle
                            currentQuestionIdx={ this.state.currentQuestionIdx }
                            totalQuestions={ this.state.surveyQuestions.length } />
                    } />
                {this.renderContent()}
            </div>
        );
    }

    renderContent = () => {
        if (this.state.isShowThankYouForAnswering) return <ThankYouForAnswering name={ i18n.language === ENGLISH ? this.props.respondent.get('firstName') : this.props.respondent.get('lastName') } />;
        if (this.state.isShowInitialExtraQuestions) return <InitialExtraQuestions initiateExtraQuestions={ this.initiateExtraQuestions } />;
        if (this.state.isShowCoachingTip) return <CoachingTip tip={ this.state.tip } />;

        const canProceed = !!this.state.questionAnswers[this.state.currentQuestionIdx];
        const canDecrease = this.state.currentQuestionIdx > 0;

        return (
            <div className='form-container' key={ this.state.currentQuestionIdx }>
                {this.state.surveyQuestions.map((question, idx) =>
                    (
                        this.state.currentQuestionIdx === idx && (
                            <QuestionContent
                                key={ idx }
                                isExtraQuestions={ this.state.isExtraQuestions }
                                hasCommentsEnabled={ this.props.surveyOptions.hasCommentsEnabled }
                                selectedOption={ this.state.questionAnswers[idx] }
                                questionComment={ this.state.questionComments[idx] }
                                selectAnswer={ this.selectAnswer }
                                clearComment={ this.clearComment }
                                handleCommentInput={ this.handleCommentInput }
                                isCurrentQuestion={ this.state.currentQuestionIdx === idx }
                                questionIdx={ idx }
                                question={ question }
                                direction={
                                    this.state.previousQuestionIdx <= this.state.currentQuestionIdx
                                        ? 'increment'
                                        : 'decrement'
                                } />
                        )
                    )
                )}
                {
                    this.state.shouldShowControls && (
                        <BackNextToggle
                            canProceed={ !this.state.isNextLocked && canProceed }
                            canDecrease={ canDecrease }
                            shiftNext={ this.shiftNext }
                            shiftBack={ this.shiftBack } />
                    )
                }
            </div>
        );
    }

    submitAnswer = async () => {
        const isSubmit = this.state.currentQuestionIdx === this.state.surveyQuestions.length - 1;

        await this.props.enhanceWithRequestErrors(this.props.actions.autoSave)({
            survey: [
                {
                    ...this.state.surveyQuestions[this.state.currentQuestionIdx],
                    optionId: this.state.questionAnswers[this.state.currentQuestionIdx],
                    comment: this.state.questionComments[this.state.currentQuestionIdx]
                }
            ],
            isSubmit: (isSubmit && this.state.isExtraQuestions) || (isSubmit && !this.props.extraQuestions.length),
            surveyId: ENGAGEMENT_SURVEY_TYPE_ID,
            type: ENGAGEMENT_SURVEY_ANSWER_URL,
            token: this.props.engagementSurveyToken
        });

        if (isSubmit && this.props.extraQuestions.length && !this.state.isExtraQuestions) {
            this.displayInitialExtraQuestions();
        } else if (isSubmit && isBatchSurvey()) {
            this.setState({
                shouldShowControls: false
            });

            this.handleTipAction();
        } else if (isSubmit) {
            this.setState({
                shouldShowControls: false
            });

            this.props.actions.goToPage(GO_TO_MY_REPORT);
        }
    }

    selectAnswer = (option) => {
        if (this.props.isRequesting) return;
        if (!option) return;

        const { questionAnswers } = this.state;
        questionAnswers[this.state.currentQuestionIdx] = option;

        this.setState({ questionAnswers });
    }

    initiateExtraQuestions = () => this.setState({
        questionAnswers: new Array(this.props.extraQuestions.length),
        questionComments: new Array(this.props.extraQuestions.length),
        surveyQuestions: this.props.extraQuestions,
        currentQuestionIdx: 0,
        isShowInitialExtraQuestions: false,
        isExtraQuestions: true
    });

    displayInitialExtraQuestions = () => this.setState({ isShowInitialExtraQuestions: true });

    shiftNext = () => {
        trackMixpanelEvent({
            eventName: ENGAGEMENT_SURVEY.ENGAGEMENT_SURVEY_CLICK_NEXT,
            properties: {
                hasEnteredComment: !!this.state.questionComments[this.state.currentQuestionIdx],
                currentQuestionIndex: this.state.currentQuestionIdx,
                nextQuestionIndex: this.state.currentQuestionIdx + 1,
                questionId: this.state.surveyQuestions[this.state.currentQuestionIdx].id,
                scaleId: this.state.surveyQuestions[this.state.currentQuestionIdx].scaleId,
                questionTitle: this.state.surveyQuestions[this.state.currentQuestionIdx].title,
                numberOfQuestions: this.state.surveyQuestions.length
            }
        });

        this.setState({
            isNextLocked: true
        });

        this.submitAnswer()
            .then(() => {
                this.setState({
                    currentQuestionIdx: this.state.currentQuestionIdx + 1,
                    previousQuestionIdx: this.state.currentQuestionIdx,
                    isNextLocked: false
                });
            });
    }

    shiftBack = () => {
        trackMixpanelEvent({
            eventName: ENGAGEMENT_SURVEY.ENGAGEMENT_SURVEY_CLICK_BACK,
            properties: {
                currentQuestionIndex: this.state.currentQuestionIdx,
                nextQuestionIndex: this.state.currentQuestionIdx - 1,
                questionId: this.state.surveyQuestions[this.state.currentQuestionIdx].id,
                scaleId: this.state.surveyQuestions[this.state.currentQuestionIdx].scaleId,
                questionTitle: this.state.surveyQuestions[this.state.currentQuestionIdx].title,
                numberOfQuestions: this.state.surveyQuestions.length
            }
        });

        this.setState({
            currentQuestionIdx: this.state.currentQuestionIdx - 1,
            previousQuestionIdx: this.state.currentQuestionIdx
        });
    }

    handleCommentInput = (e) => {
        const { questionComments } = this.state;
        questionComments[this.state.currentQuestionIdx] = e.target.value;
        this.setState({ questionComments });

        clearTimeout(this.commentChangeDebounceRef.current);
        this.commentChangeDebounceRef.current = setTimeout(() => {
            trackMixpanelEvent({
                eventName: ENGAGEMENT_SURVEY.ENGAGEMENT_SURVEY_COMMENT_CHANGED,
                properties: {
                    currentQuestionIndex: this.state.currentQuestionIdx,
                    questionId: this.state.surveyQuestions[this.state.currentQuestionIdx].id,
                    scaleId: this.state.surveyQuestions[this.state.currentQuestionIdx].scaleId,
                    questionTitle: this.state.surveyQuestions[this.state.currentQuestionIdx].title,
                    numberOfQuestions: this.state.surveyQuestions.length
                }
            });
        }, 700);
    }

    clearComment = () => {
        const { questionComments } = this.state;
        questionComments[this.state.currentQuestionIdx] = null;
        this.setState({ questionComments });

        trackMixpanelEvent({
            eventName: ENGAGEMENT_SURVEY.ENGAGEMENT_SURVEY_CLICK_COMMENT_CLEAR,
            properties: {
                currentQuestionIndex: this.state.currentQuestionIdx,
                questionId: this.state.surveyQuestions[this.state.currentQuestionIdx].id,
                scaleId: this.state.surveyQuestions[this.state.currentQuestionIdx].scaleId,
                questionTitle: this.state.surveyQuestions[this.state.currentQuestionIdx].title,
                numberOfQuestions: this.state.surveyQuestions.length
            }
        });
    }

    handleTipAction = async () => {
        try {
            const tip = await getEngagementSurveyTips(this.props.engagementSurveyToken);
            if (tip) {
                this.setState({ isShowCoachingTip: true, tip });
            } else {
                this.setState({ isShowThankYouForAnswering: true });
            }
        } catch (error) {
            this.setState({ isShowCoachingTip: true, tip: null });
        }
    }
}

Engagement.propTypes = {
    surveyQuestions: PropTypes.array,
    engagementSurveyToken: PropTypes.string,
    isRequesting: PropTypes.bool,
    respondent: PropTypes.object,
    surveyOptions: PropTypes.object,
    extraQuestions: PropTypes.array,
    lastQuestion: PropTypes.number,
    ...withRequestErrorsPropTypes
};

Engagement.defaultProps = {
    surveyQuestions: [],
    extraQuestions: [],
    engagementSurveyToken: null,
    isRequesting: null,
    respondent: PropTypes.object,
    surveyOptions: {},
    lastQuestion: null
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(Object.assign({}, surveyActions, pageActions), dispatch)
});

const mapStateToProps = (state) => ({
    respondent: state.respondentNameReducer,
    surveyQuestions: state.surveyReducer.toJS().likert,
    extraQuestions: state.surveyReducer.toJS().directQuestions,
    engagementSurveyToken: state.respondentNameReducer.get('engagementSurveyToken'),
    surveyOptions: state.surveyOptionsReducer.toJS(),
    lastQuestion: state.surveyReducer.toJS().lastEngagementQuestion
});

export default connect(mapStateToProps, mapDispatchToProps)(withRequestErrors(Engagement));

